import { useContext, useEffect, useState } from "react";
import { displayCaseOutcomeStatus, displayDateString, formatDateBackend, getCaseLockInfo, parseDate } from "Backend";
import {
  Badge,
  Button,
  Checkbox,
  FormControlLabel,
  IconButton,
  Menu,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from "@mui/material";
import theme, { DataPendoIconButtonProps, EllipsisSX } from "theme";
import SkeletonRow from "components/SkeletonRow";
import SimpleMenu, { SimpleMenuItem } from "components/SimpleMenu";
import EditIcon from "@mui/icons-material/Edit";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { selectEnableFirmFeatureSet, selectIsSupport } from "redux/slices/session";
import {
  CaseFilterQueryParam,
  deleteClientLensCase,
  fetchClientLens,
  fetchClientLensCases,
  selectCasePropertySchemas,
  selectClientLens,
  setCaseFilters,
} from "redux/slices/clientLens";
import { Delete, Download, FilterList, PersonAddAlt, RestartAlt, Search } from "@mui/icons-material";
import { Case, CaseClientStatus, CustomCaseProperty } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { ConfirmationDialogContext } from "contexts/ConfirmationDialogContext";
import { P } from "theme/typography";
import CaseEditInfoDialogWrapper from "routes/lenses/lens/case/CaseInfoEditWrapper";
import { StackedAssigneeChips } from "SBR/AssigneeChip";
import { usePopover } from "helpers/hooks/usePopover";
import { useSectionValues } from "SBR/useSectionValues";
import { cn } from "components/lib/utils";
import SBR from "SBR/SBR";
import { selectFormId, selectOutcomeEnabledFormSections } from "redux/slices/form.slice";
import { fullNameFromParts } from "helpers/name-helpers";
import CaseTableFilterEnhanced from "routes/lenses/lens/admin/CaseTableFilter";
import { useNavigate } from "react-router";
import { AppRoutes } from "AppRoutes";
import { useSnackbar } from "notistack";
import { useCases } from "./useCases";
import { pluralize } from "helpers/coll";
import CheckAll from "routes/lenses/lens/files/CheckAll";
import { BulkExportModal } from "components/BulkExportModal";
import { BulkCaseClientStatusUpdate } from "components/BulkCaseClientStatusUpdateModal";
import { BulkAssignCasesModal } from "./BulkAssignCasesModal";
import CaseFilters from "./CaseFilters";
import { useSearchParams } from "react-router-dom";
import { CaseIdUpdateDialog } from "components/CaseIdUpdateDialog";

const CaseRow = (props: {
  case: Case;
  selected: boolean;
  onSelect: (ccase: Case) => void;
  disabled: boolean;
  navigateToCase: (caseId: number, docs: boolean) => void;
  actions: SimpleMenuItem[];
  isDefenseFirm?: boolean;
  caseProperties: CustomCaseProperty[];
}) => {
  const caseLockedInfo = getCaseLockInfo(props.caseProperties, props.case);
  const formId = useAppSelector(selectFormId);
  const outcomeEnabledFormSections = useAppSelector(selectOutcomeEnabledFormSections);
  const c = props.case;
  const info = c.info;
  const hasDocs = c.documentCount !== 0;
  const popover = usePopover();
  const sectionValues = useSectionValues(c.sectionValues);
  const caseInactive = c.clientStatus === CaseClientStatus.Inactive;

  return (
    <>
      <TableRow hover key={c.id} selected={false}>
        <TableCell sx={{ px: 0 }}>
          <Checkbox
            disabled={props.disabled}
            checked={props.selected}
            onChange={() => {
              props.onSelect(c);
            }}
            size="small"
            color="primary"
          />
        </TableCell>
        {!caseLockedInfo.locked ? (
          <TableCell align="left">
            <SimpleMenu hamburger items={props.actions} dataPendo="case-table-row-action-button" />
          </TableCell>
        ) : (
          <TableCell />
        )}
        {caseLockedInfo.locked && caseLockedInfo.unlockDate ? (
          <Tooltip title={`Unlock date: ${formatDateBackend(parseDate(caseLockedInfo.unlockDate))}`} arrow>
            <TableCell sx={EllipsisSX}>{info.clientCaseId}</TableCell>
          </Tooltip>
        ) : (
          <TableCell
            data-pendo="case-table-row-case-id"
            sx={{
              ...theme.mixins.linkLike,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={info.clientCaseId}
            onClick={() => {
              props.navigateToCase(c.id, !hasDocs);
            }}>
            {info.clientCaseId}
          </TableCell>
        )}
        {props.isDefenseFirm && (
          <TableCell
            title={c.plaintiffFirm?.name}
            sx={EllipsisSX}
            className={cn(c.clientStatus === CaseClientStatus.Inactive ? "text-gray-200" : "")}>
            {c.plaintiffFirm?.name}
          </TableCell>
        )}
        <TableCell
          className={cn(caseInactive ? "text-gray-200" : "")}
          sx={{
            overflow: { xs: "hidden" },
            textOverflow: { xs: "ellipsis" },
            whiteSpace: { xs: "nowrap" },
          }}>
          {fullNameFromParts(info.claimantGivenName, info.claimantSurname)}
        </TableCell>
        <TableCell className={cn(caseInactive ? "text-gray-200" : "")}>
          {info.dateOfBirth ? displayDateString(info.dateOfBirth) : ""}
        </TableCell>
        <TableCell
          sx={EllipsisSX}
          className="cursor-pointer"
          onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
          <p className={cn(c.clientStatus === "active" ? "pattern-link text-peri-900 font-bold" : "text-gray-200")}>
            {sectionValues.getSections()}
          </p>
        </TableCell>
        <TableCell className="cursor-pointer" onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)}>
          <StackedAssigneeChips
            qaSections={sectionValues.qaSections}
            reviewSections={sectionValues.reviewSections}
            completeSections={sectionValues.completedSections}
            deEmphasize={c.clientStatus === "inactive"}
          />
        </TableCell>
        <TableCell className={cn(caseInactive ? "text-gray-200" : "")}>
          {c.outcomeStatus && displayCaseOutcomeStatus(c.outcomeStatus)}
        </TableCell>
        <TableCell className={cn("text-center", caseInactive ? "text-gray-200" : "")}>
          {c.documentCount > 0 && c.documentCount}
        </TableCell>
      </TableRow>
      <Popover
        open={popover.isOpen}
        anchorEl={popover.anchorEl}
        onClose={popover.onPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}>
        {formId !== undefined && (
          <div className="w-96">
            <SBR
              formId={formId}
              lensCase={c}
              sectionValues={c.sectionValues}
              formSections={outcomeEnabledFormSections}
            />
          </div>
        )}
      </Popover>
    </>
  );
};

export default function SBRCaseTable({
  lensId,
  bulkActionsEnabled,
  staticAssignees,
  reviewer = false,
}: {
  lensId: number;
  bulkActionsEnabled: boolean;
  staticAssignees: number[];
  reviewer?: boolean;
}) {
  const confirmCtx = useContext(ConfirmationDialogContext);
  const [editOpen, setEditOpen] = useState(false);
  const isSupport = useAppSelector(selectIsSupport);
  const snackpatch = useAppDispatchWithSnackbar();
  const dispatch = useAppDispatch();
  const [searchParams, _] = useSearchParams();
  const [selectedCase, setSelectedCase] = useState<Case | undefined>(undefined);
  const caseProperties = useAppSelector(selectCasePropertySchemas);
  const navigate = useNavigate();
  const clientLens = useAppSelector(selectClientLens);
  const showFirms = useAppSelector(selectEnableFirmFeatureSet);
  const [caseDownloadMenuAnchorEl, setCaseDownloadMenuAnchorEl] = useState<null | HTMLElement>(null);
  const casesHook = useCases(lensId, staticAssignees, reviewer);
  const [exportCaseProfileModalOpen, setExportCaseProfileModalOpen] = useState(false);
  const [assignCasesModalOpen, setAssignCasesModalOpen] = useState(false);
  const [bulkStatusModalOpen, setBulkStatusModalOpen] = useState(false);
  const [editCaseIdOpen, setEditCaseIdOpen] = useState(false);
  const navigateToCase = (caseId: number) => {
    navigate(AppRoutes.caseDetailBuilder(clientLens.id, caseId, ""));
  };
  const { enqueueSnackbar } = useSnackbar();
  const filterPopover = usePopover();
  const handleCaseDownloadClick = () => {
    setCaseDownloadMenuAnchorEl(null);
    casesHook
      .handleCaseDownload()
      .then(() => enqueueSnackbar("Case list downloaded successfully", { variant: "success" }))
      .catch(() => enqueueSnackbar("Error downloading case list ", { variant: "error" }));
  };
  const canDoBulkActions = casesHook.selectAllCases || casesHook.selectedCases.length > 0;

  const fetchCases = () => {
    dispatch(setCaseFilters(searchParams.toString()));
    return dispatch(fetchClientLensCases(casesHook.getLensCasesQueryVariables));
  };

  const onBulkClientCaseStatusUpdate = async (status: CaseClientStatus) => {
    try {
      await casesHook.bulkCaseClientStatusUpdate(status);
      await Promise.all([fetchCases(), dispatch(fetchClientLens({ lensId })).unwrap()]);
      casesHook.deselectAllCases();
    } catch (e) {
      console.error(e);
      enqueueSnackbar("There was a problem updating the cases. Please refresh and try again.", { variant: "error" });
    }
  };

  useEffect(() => {
    const thunk = fetchCases();
    thunk.unwrap().catch((err) => {
      if (err.name !== "AbortError") {
        enqueueSnackbar(`Error loading cases: ${err["message"]}`, { variant: "error" });
      }
    });
    return () => thunk.abort();
  }, [lensId, casesHook.caseFilterParams]);

  const actionItems = (ccase: Case) => {
    const items: SimpleMenuItem[] = [
      {
        name: "Edit",
        disabled: false,
        dataPendo: "case-table-row-edit-button",
        icon: <EditIcon />,
        action: () => {
          setSelectedCase(ccase);
          setEditOpen(true);
        },
      },
    ];

    if (isSupport) {
      items.push({
        name: "Update Case ID",
        disabled: false,
        dataPendo: "case-table-row-case-id-edit-button",
        icon: <EditIcon />,
        action: () => {
          setSelectedCase(ccase);
          setEditCaseIdOpen(true);
        },
      });
    }

    if (isSupport) {
      items.push({
        name: "Delete",
        disabled: false,
        dataPendo: "case-table-row-delete-button",
        icon: <Delete />,
        action: () => {
          confirmCtx.confirm({
            title: "Delete Case",
            prompt: (
              <>
                <P variant="body2">Are you sure you want to delete the following case?</P>
                <P>
                  {ccase.info.claimantGivenName} {ccase.info.claimantSurname} ({ccase.info.clientCaseId})
                </P>
              </>
            ),
            confirmButtonText: "Delete",
            callback: () => {
              return snackpatch(deleteClientLensCase({ caseId: ccase.id }), {
                successMessage: "Case deleted",
                errorMessage: "Unable to delete case",
              });
            },
          });
        },
      });
    }
    return items;
  };

  const rows = () => {
    if (casesHook.casesLoading) {
      return Array(8)
        .fill(0)
        .map((_x, idx) => <SkeletonRow key={idx} columnNum={showFirms ? 9 : 8} />);
    } else {
      return casesHook.cases.map((ccase) => {
        return (
          <CaseRow
            onSelect={(ccase) => {
              if (casesHook.selectedCases.find((i) => i.id === ccase.id)) {
                casesHook.setSelectedCases(casesHook.selectedCases.filter((i) => i.id !== ccase.id));
              } else {
                casesHook.setSelectedCases([...casesHook.selectedCases, ccase]);
              }
            }}
            disabled={casesHook.selectAllCases}
            selected={casesHook.selectAllCases ? true : !!casesHook.selectedCases.find((i) => i.id === ccase.id)}
            caseProperties={caseProperties}
            key={ccase.id}
            case={ccase}
            navigateToCase={navigateToCase}
            actions={actionItems(ccase)}
            isDefenseFirm={showFirms}
          />
        );
      });
    }
  };

  const showEmptyState = !casesHook.casesLoading && casesHook.cases.length === 0;

  return (
    <div className="flex flex-col gap-2 h-full">
      <div className="flex gap-2 items-center">
        <div className="w-72">
          <CaseTableFilterEnhanced
            label="Search by Claimant Name or Case ID"
            selection={casesHook.caseFilterParams.textQuery || ""}
            setSelection={(value) => casesHook.addFilter(CaseFilterQueryParam.SEARCH, value)}
            lensId={lensId}
          />
        </div>
        <IconButton onClick={(evt) => filterPopover.onPopoverOpen(evt.currentTarget)}>
          <Badge showZero={false} badgeContent={casesHook.filtersApplied}>
            <FilterList />
          </Badge>
        </IconButton>
        <CaseFilters
          filterPopover={filterPopover}
          lensId={lensId}
          staticAssignees={staticAssignees}
          casesHook={casesHook}
          readonly={false}
        />
        {casesHook.nonDefaultFiltersApplied > 0 && (
          <Button
            color="default"
            startIcon={<RestartAlt />}
            className="ml-1"
            size="small"
            onClick={casesHook.resetFilters}
            variant="outlined">
            Reset Filters
          </Button>
        )}
        <div className="grow" />
        <TablePagination
          sx={{
            borderBottom: 0,
            ".MuiTablePagination-spacer": {
              flex: 0,
            },
          }}
          count={casesHook.casesTotal}
          rowsPerPage={casesHook.caseFilterParams.rowsPerPage}
          page={casesHook.caseFilterParams.page}
          onPageChange={(_evt, page) => casesHook.onPageChange(page)}
          onRowsPerPageChange={(evt) => casesHook.onCasesPerPageChange(evt.target.value)}
          labelRowsPerPage="Cases per page"
          slotProps={{
            actions: {
              nextButton: {
                "data-pendo": "case-table-pagination-next",
              } as DataPendoIconButtonProps,
              previousButton: {
                "data-pendo": "case-table-pagination-previous",
              } as DataPendoIconButtonProps,
            },
          }}
        />
      </div>
      {bulkActionsEnabled && (
        <div className="flex gap-2 items-center">
          <div className="mr-4">
            {casesHook.selectAllCases ? (
              <Checkbox checked disabled size="small" color="primary" />
            ) : (
              <CheckAll<Case>
                checkboxProps={{
                  color: "primary",
                }}
                selectedItems={casesHook.selectedCases}
                items={casesHook.cases}
                setItems={casesHook.setSelectedCases}
                compareFn={(a, b) => a.id === b.id}
              />
            )}
          </div>
          <Button
            variant="outlined"
            disabled={!canDoBulkActions}
            size="small"
            startIcon={<PersonAddAlt />}
            onClick={() => setAssignCasesModalOpen(true)}
            data-pendo="case-table-assign-cases">
            Assign Cases
          </Button>
          {assignCasesModalOpen && (
            <BulkAssignCasesModal
              open={assignCasesModalOpen}
              setOpen={setAssignCasesModalOpen}
              lensId={lensId}
              casesHook={casesHook}
            />
          )}
          <Button
            variant="outlined"
            disabled={!canDoBulkActions}
            size="small"
            startIcon={<Download />}
            onClick={(event) => setCaseDownloadMenuAnchorEl(event.currentTarget)}
            data-pendo="case-table-download-button">
            Download Case List
          </Button>
          <Button
            variant="outlined"
            disabled={!canDoBulkActions}
            size="small"
            data-pendo="case-table-generate-exports"
            onClick={() => setExportCaseProfileModalOpen(true)}>
            Generate Exports
          </Button>
          {exportCaseProfileModalOpen && (
            <BulkExportModal
              lensId={lensId}
              caseFilters={casesHook.getCasesParamsInput}
              casesTotal={casesHook.selectAllCases ? casesHook.casesTotal : casesHook.selectedCases.length}
              open={exportCaseProfileModalOpen}
              setOpen={setExportCaseProfileModalOpen}
            />
          )}
          <Button
            variant="outlined"
            disabled={!canDoBulkActions}
            size="small"
            onClick={() => setBulkStatusModalOpen(true)}
            data-pendo="case-table-active-inactive">
            Mark Active/Inactive
          </Button>
          {bulkStatusModalOpen && (
            <BulkCaseClientStatusUpdate
              open={bulkStatusModalOpen}
              casesTotal={casesHook.selectAllCases ? casesHook.casesTotal : casesHook.selectedCases.length}
              close={() => setBulkStatusModalOpen(false)}
              onUpdate={onBulkClientCaseStatusUpdate}
            />
          )}
          <Menu
            anchorEl={caseDownloadMenuAnchorEl}
            open={Boolean(caseDownloadMenuAnchorEl)}
            onClose={() => setCaseDownloadMenuAnchorEl(null)}>
            <div className="flex flex-col gap-2 p-4 pt-0 pb-2">
              <FormControlLabel
                label="Include Custom Properties"
                control={
                  <Checkbox
                    checked={casesHook.includePropertiesCheckbox}
                    onChange={(event) => casesHook.setIncludePropertiesCheckbox(event.target.checked)}></Checkbox>
                }></FormControlLabel>
              <div>
                <Tooltip
                  title={`Download ${casesHook.selectAllCases ? casesHook.casesTotal.toLocaleString() : casesHook.selectedCases.length.toLocaleString()} ${pluralize(casesHook.selectedCases.length, "case")}`}>
                  <Button variant="contained" onClick={handleCaseDownloadClick}>
                    Download
                  </Button>
                </Tooltip>
              </div>
            </div>
          </Menu>
        </div>
      )}
      {casesHook.selectedCases.length > 0 && (
        <div className="flex bg-gray-50 p-4 items-center justify-center">
          <div className="flex gap-2 items-center">
            <p className="text-sm">
              {casesHook.selectAllCases
                ? `All ${casesHook.casesTotal.toLocaleString()}`
                : `${casesHook.selectedCases.length.toLocaleString()}`}{" "}
              {pluralize(casesHook.selectedCases.length, "case")} selected.
            </p>
            {casesHook.selectAllCases ? (
              <p
                className="text-sm font-semibold text-peri-900 cursor-pointer"
                onClick={() => {
                  casesHook.setSelectAllCases(false);
                  casesHook.setSelectedCases([]);
                }}>
                Clear selection
              </p>
            ) : !casesHook.selectAllCases && casesHook.selectedCases.length !== casesHook.casesTotal ? (
              <p
                className="text-sm font-semibold text-peri-900 cursor-pointer"
                onClick={() => casesHook.setSelectAllCases(true)}>
                Select all {casesHook.casesTotal.toLocaleString()} cases in the {clientLens.name} case table.
              </p>
            ) : (
              <></>
            )}
          </div>
        </div>
      )}
      <div className="h-full min-h-0 flex flex-col">
        <TableContainer>
          <Table stickyHeader size="small" sx={{ tableLayout: { xs: "fixed" } }}>
            <TableHead>
              <TableRow>
                <TableCell className="p-0 w-9" />
                <TableCell>Action</TableCell>
                <TableCell>Case ID</TableCell>
                {showFirms && <TableCell>Firm</TableCell>}
                <TableCell>Claimant</TableCell>
                <TableCell title="Date of Birth">DOB</TableCell>
                <TableCell>Sections</TableCell>
                <TableCell>Assignees</TableCell>
                <TableCell>Overall Outcome</TableCell>
                <TableCell>Documents</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows()}
              {showEmptyState && (
                <tr>
                  <td colSpan={10}>
                    <div className="flex flex-col w-full items-center justify-center h-48 text-gray-400">
                      <Search className="h-16 w-16" />
                      <span>No cases match your filters</span>
                    </div>
                  </td>
                </tr>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>

      {selectedCase && editOpen && (
        <CaseEditInfoDialogWrapper
          ccase={selectedCase}
          caseAssignees={casesHook.assignees}
          onClose={() => setEditOpen(false)}
          open={editOpen}
        />
      )}
      {selectedCase && (
        <CaseIdUpdateDialog mycase={selectedCase} open={editCaseIdOpen} onClose={() => setEditCaseIdOpen(false)} />
      )}
    </div>
  );
}
